import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Eye, EyeOff } from "lucide-react";

// Password strength evaluator
const getPasswordStrength = (password) => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;

  const strengthLevels = ["Very Weak", "Weak", "Fair", "Good", "Strong", "Very Strong"];
  const colors = ["#dc2626", "#f97316", "#eab308", "#3b82f6", "#10b981", "#047857"];

  return {
    score,
    label: strengthLevels[score],
    color: colors[score],
  };
};

function SignUp() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: "", color: "" });
  const navigate = useNavigate();

  const validatePassword = (password) => {
    const minLength = 8;
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);

    return (
      password.length >= minLength &&
      hasSpecialChar &&
      hasUpperCase &&
      hasLowerCase &&
      hasNumber
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }

    if (!validatePassword(password)) {
      setError(
        "Password must be at least 8 characters long, include uppercase, lowercase, a number, and a special character."
      );
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/register`, {
        name,
        email,
        password,
      });

      setShowModal(true);
    } catch (err) {
      console.error(err);
      setError("Registration failed. Please try again.");
    }
  };

  return (
    <div className="flex flex-col md:flex-row justify-center items-center bg-gray-100 min-h-screen p-6"
      style={{
        backgroundImage: "url('/assets/img/bg-3.jpg')",
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
      }}>
      {/* Logo Section */}
      <div className="flex flex-col items-center mb-10 md:mb-0 md:mr-10">
        <img
          src="/assets/img/oneHub_logo1.png"
          alt="Logo"
          className="w-32 h-32 mb-4 rounded-full shadow-lg" />
        <h1 className="text-4xl font-bold text-center bg-white px-4 py-2 rounded shadow">
          <span className="bg-black text-gray-100 px-2 rounded">OneLegal</span>Project
        </h1>
        <h2 className="text-2xl font-bold mb-6 text-center text-black">Client Registration</h2>
      </div>

      {/* Form Section */}
      <div className="bg-gray-100 p-8 rounded shadow-md w-full max-w-md">
        <form onSubmit={handleSubmit}>
          <h2 className="text-2xl font-bold mb-6 text-center text-black">Registration</h2>

          <div className="mb-4">
            <label className="block font-semibold text-gray-800">Name</label>
            <input
              type="text"
              placeholder="Enter your name"
              className="w-full p-2 border border-gray-300 rounded"
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="mb-4">
            <label className="block font-semibold text-gray-800">Email</label>
            <input
              type="email"
              placeholder="Enter your email"
              className="w-full p-2 border border-gray-300 rounded"
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          {/* Password */}
          <div className="mb-4 relative">
            <label className="block font-semibold text-gray-800">Password</label>
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Enter your password"
              className="w-full p-2 pr-10 border border-gray-300 rounded"
              onChange={(e) => {
                const pwd = e.target.value;
                setPassword(pwd);
                setPasswordStrength(getPasswordStrength(pwd));
              }}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-2 top-9 text-gray-600"
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>

            {/* Password Strength Meter */}
            {password && (
              <div className="mt-2">
                <div className="w-full bg-gray-200 rounded h-2">
                  <div
                    className="h-2 rounded"
                    style={{
                      width: `${(passwordStrength.score / 5) * 100}%`,
                      backgroundColor: passwordStrength.color,
                      transition: "width 0.3s ease",
                    }}
                  />
                </div>
                <p className="text-sm mt-1" style={{ color: passwordStrength.color }}>
                  Strength: {passwordStrength.label}
                </p>
              </div>
            )}
          </div>

          {/* Confirm Password */}
          <div className="mb-4 relative">
            <label className="block font-semibold text-gray-800">Confirm Password</label>
            <input
              type={showConfirmPassword ? "text" : "password"}
              placeholder="Confirm your password"
              className="w-full p-2 pr-10 border border-gray-300 rounded"
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-2 top-9 text-gray-600"
            >
              {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

          <button
            type="submit"
            className="w-full bg-black text-white py-2 rounded hover:bg-gray-800 transition"
          >
            Register
          </button> 
        </form>

        <p className="mt-4 text-center text-sm">Already have an account?</p>
        <Link
          to="/clientlogin"
          className="block mt-2 w-full text-center bg-white text-black border border-gray-300 py-2 rounded hover:bg-gray-100"
        >
          Login
        </Link>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-full max-w-md shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-yellow-500">Registration Successful</h3>
            <p className="mb-6">Are you sure you want to go to the login page?</p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
              >
                Cancel
              </button>
              <button
                onClick={() => navigate("/clientlogin")}
                className="px-4 py-2 bg-black text-white rounded hover:bg-gray-800"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SignUp;
